import {
  LeftBarProfileDropdown,
  LeftBarProfileDropdownItem,
  LeftBarSeparator,
} from "components/Sidebar/LeftBar";
import { Settings } from "components/icons/Navbar/Settings";
import { Billing } from "components/icons/Navbar/Billing";
import { Team } from "components/icons/Navbar/Team";
import { Perks } from "components/icons/Navbar/Perks";
import { Logout } from "components/icons/Navbar/Logout";
import { useAuth } from "hooks/useAuth";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import Loader from "components/DesignSystem/Loader/loader";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useMyPermissions } from "hooks/useMyPermissions";
import { useContext } from "react";
import { useLocation } from "react-router-dom";
import authContext from "jwt_context&axios/authContext";
import { USER_PROFILE } from "constants/userTypes";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { Ticket } from "components/icons/LeftNav/Ticket";
import { GridFour } from "components/icons/GridFour";
import { IntegrationIcon } from "components/icons/LeftNav/IntegrationIcon";
import { ListIcon } from "components/icons/ListIcon";
import ClaudeIconImg from "static/images/ClaudeIcon.svg";

const ClaudeMenuIcon = () => (
  <img src={ClaudeIconImg} alt="Claude" className="t-h-4 t-w-4" />
);

export const ProfileDropdown = ({
  useInternalLink,
}: {
  useInternalLink?: boolean;
}) => {
  const auth = useAuth();
  const group = useCurrentGroupContext();
  const { isCustomer, isAdmin, isCpa } = useRoleBasedView();
  const { role, isSuperAdmin } = useMyPermissions();

  const { logoutUser } = useContext(authContext);
  const { link } = useConstructInternalLink();
  const location = useLocation();

  if (!auth) {
    return <Loader size="small" />;
  }

  const { name, email, profile_url } = auth;
  const { name: groupName } = group;

  const mayBeUseLink = useInternalLink ? link : (path: string) => path;

  const canConnectClaude = isCustomer || isAdmin || isCpa;

  let roleLabel: string | null = null;
  if (isCustomer) {
    roleLabel = role?.name ?? (isSuperAdmin ? "Super Admin" : null);
  }

  return (
    <LeftBarProfileDropdown
      email={email}
      name={name}
      imageUrl={profile_url || USER_PROFILE}
      org={isCustomer ? groupName : null}
      role={roleLabel}
    >
      {isCustomer && (
        <LeftBarProfileDropdownItem
          useParentPath={false}
          icon={GridFour}
          to={mayBeUseLink("/settings/services")}
        >
          Products & Services
        </LeftBarProfileDropdownItem>
      )}
      {isCustomer && (
        <LeftBarProfileDropdownItem
          useParentPath={false}
          icon={Billing}
          to={mayBeUseLink("/settings/billing")}
        >
          Billing
        </LeftBarProfileDropdownItem>
      )}
      {isCustomer && (
        <LeftBarProfileDropdownItem
          useParentPath={false}
          icon={Perks}
          to={link("/settings/billing/referralrewards", {
            moreQuery: {
              settings_back_to: location.pathname + location.search,
            },
          })}
        >
          Referrals
        </LeftBarProfileDropdownItem>
      )}
      {isCustomer && (
        <LeftBarProfileDropdownItem
          useParentPath={false}
          icon={Perks}
          to={mayBeUseLink("/community/perks")}
        >
          Perks
        </LeftBarProfileDropdownItem>
      )}

      {isCustomer && <LeftBarSeparator />}

      <LeftBarProfileDropdownItem
        useParentPath={false}
        icon={Settings}
        to={mayBeUseLink("/settings/profile")}
      >
        Profile
      </LeftBarProfileDropdownItem>
      {isCustomer && (
        <LeftBarProfileDropdownItem
          useParentPath={false}
          icon={Team}
          to={mayBeUseLink("/settings/team")}
        >
          Team
        </LeftBarProfileDropdownItem>
      )}
      {canConnectClaude && (
        <LeftBarProfileDropdownItem
          useParentPath={false}
          icon={ClaudeMenuIcon}
          to={mayBeUseLink("/settings/claude-integration")}
        >
          Connect to Claude
        </LeftBarProfileDropdownItem>
      )}
      {isCustomer && (
        <LeftBarProfileDropdownItem
          useParentPath={false}
          icon={IntegrationIcon}
          to={mayBeUseLink("/settings/api-settings")}
        >
          API access
        </LeftBarProfileDropdownItem>
      )}

      <LeftBarSeparator />

      {isCustomer && (
        <LeftBarProfileDropdownItem
          useParentPath={false}
          icon={ListIcon}
          to={mayBeUseLink("/settings/auditlog")}
        >
          Audit log
        </LeftBarProfileDropdownItem>
      )}
      {isAdmin && (
        <LeftBarProfileDropdownItem
          useParentPath={false}
          icon={ListIcon}
          to={mayBeUseLink("/admin/auditlog")}
        >
          Audit log
        </LeftBarProfileDropdownItem>
      )}
      {isCustomer && (
        <LeftBarProfileDropdownItem
          useParentPath={false}
          icon={Ticket}
          to={mayBeUseLink("/settings/tickets")}
        >
          Support tickets
        </LeftBarProfileDropdownItem>
      )}

      <LeftBarSeparator />

      <LeftBarProfileDropdownItem
        onClick={logoutUser}
        useParentPath={false}
        type="danger"
        icon={Logout}
      >
        Log out
      </LeftBarProfileDropdownItem>
    </LeftBarProfileDropdown>
  );
};
